import Decimal from 'decimal.js';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InlineNotice from '../../../../components/InlineNotice';
import Input from '../../../../components/Input';
import ViewportAttachedButton from '../../../../components/ViewportAttachedButton';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import TypesService from '../../../../services/TypesService';
import { sendSlice } from '../../../../store/slices';
import SwapButton from '../../../SwapButton';

import './PaymentConfiguration.scss';
import { createRootModal } from '../../RootModal';

const CurrenciesById = TypesService.CurrenciesById;

const PaymentConfiguration = createRootModal(({ handleClose }) => {
	const [feeAmount, setFeeAmount] = useState('0');
	const [convertedFeeAmount, setConvertedFeeAmount] = useState('0');
	const [dontShowWarnings, setDontShowWarnings] = useState(true);

	const paymentConfigurationRef = useRef(null);

	const feeData = useSelector(store => store.send.feeData);
	const [inputInUsd, setInputInUsd] = useState(feeData.InputInUsd);

	const quotes = useSelector(store => store.app.quotes);

	const dispatch = useDispatch();

	const currencyRate = quotes.find(quote => quote.Id === feeData.CurrencyId)?.Rate || 0;
	const currencyData = CurrenciesById[feeData.CurrencyId];

	const currencyPrecision = currencyData.Precision;
	const inputPrecision = inputInUsd ? TypesService.FiatPrecision : currencyPrecision;
	const convertedPrecision = inputInUsd ? currencyPrecision : TypesService.FiatPrecision;

	const inputCurrency = inputInUsd ? '$' : currencyData.Name;
	const convertedCurrency = inputInUsd ? currencyData.Name : '$';

	const numericFeeAmount = Number(feeAmount) || 0; // number

	const onFeeAmountFocus = e => {
		const value = e.target.value;
		if (value === '0') setFeeAmount('');
	};

	const updateFeeAmount = value => {
		const commaReplaced = value.replace(',', '.');
		const irrelevantSymbolRemoved = commaReplaced.replace(/[^.\d]/g, '');
		const excessDotRemoved = irrelevantSymbolRemoved.replace(/(?<!^[^.]*)\./g, '');
		const formatedForCurrency = excessDotRemoved.replace(new RegExp('(\\.[^.]{' + inputPrecision + '}).*'), '$1');
		setFeeAmount(formatedForCurrency);

		const deciamlFeeAmount = new Decimal(formatedForCurrency || 0); // Decimal
		const decimalConvertedFeeAmount = inputInUsd
			? deciamlFeeAmount.dividedBy(currencyRate)
			: deciamlFeeAmount.mul(currencyRate); // Decimal

		const convertedFeeAmount = decimalConvertedFeeAmount.toFixed(convertedPrecision);
		setConvertedFeeAmount(convertedFeeAmount);
	};

	const onFeeAmountChange = e => {
		const value = e.target.value;
		updateFeeAmount(value);
	};

	const onFeeAmountBlur = e => {
		const value = e.target.value;
		const excessStartZerosRemoved = value.replace(/^0+(?=\d)/, '');
		const startZeroAddingIfDotInStart = excessStartZerosRemoved.replace(/^\./, '0.');
		setFeeAmount(startZeroAddingIfDotInStart || '0');
	};

	const focusInput = () => {
		const inputFilend = paymentConfigurationRef?.currency?.getElementsByClassName('SendAmountInput__fee-input')[0];
		if (inputFilend) inputFilend.current.focus();
	};

	useEffect(() => {
		const feeAmount = inputInUsd ? feeData.ConvertedFee : feeData.Fee;
		updateFeeAmount(new Decimal(feeAmount || 0).toString());

		focusInput();

		setTimeout(() => {
			setDontShowWarnings(false);
		}, 300);
	}, []);

	const swapInputCurrency = () => {
		setInputInUsd(!inputInUsd);

		const newFeeAmount = convertedFeeAmount;
		const newConvertedFeeAmount = feeAmount;

		setFeeAmount(newFeeAmount);
		setConvertedFeeAmount(newConvertedFeeAmount);
	};

	const done = () => {
		const newFee = Number(inputInUsd ? convertedFeeAmount : feeAmount);

		dispatch(
			sendSlice.actions.setFeeData({
				...feeData,
				Fee: newFee,
				ConvertedFee: Number(inputInUsd ? feeAmount : convertedFeeAmount),
				InputInUsd: inputInUsd,
				FeeRedacted: feeData.Fee !== newFee,
			})
		);
		handleClose();
	};

	return (
		<div className='PaymentConfiguration' ref={paymentConfigurationRef}>
			<div className='PaymentConfiguration__body'>
				<div className='PaymentConfiguration__fee-header'>
					{t('Transaction fee')}
					{` (${inputCurrency === '$' ? 'USD' : inputCurrency})`}
				</div>
				<Input
					className='SendAmountInput__fee-input FormBlock'
					placeholder='0'
					value={feeAmount}
					onFocus={onFeeAmountFocus}
					onChange={onFeeAmountChange}
					onBlur={onFeeAmountBlur}
					postfix={<SwapButton onClick={swapInputCurrency} />}
					inputMode='decimal'
					maxLength={16}
					autoFocus
				/>
				<div className='PaymentConfiguration__converted-amount'>
					{`${inputInUsd ? '' : convertedCurrency} ${HelperService.FormatCrypto(
						convertedFeeAmount,
						convertedPrecision,
						'floor'
					)} ${inputInUsd ? currencyData.Name : ''}`}
				</div>
				{!!feeAmount &&
					!dontShowWarnings &&
					(!numericFeeAmount ? (
						<InlineNotice
							className='PaymentConfiguration__fee-warning'
							type='error'
							icon='EXCLAMATIONMARK_TRIANGLE_FILL'
						>
							{t('Transaction cannot be confirmed with this fee.')}
						</InlineNotice>
					) : feeData.DefaultFee > (inputInUsd ? convertedFeeAmount : feeAmount) ? (
						<InlineNotice className='PaymentConfiguration__fee-warning'>
							{t('With this fee, confirmation may take longer than usual.')}
						</InlineNotice>
					) : null)}
			</div>
			<ViewportAttachedButton disabled={!numericFeeAmount} onClick={done}>
				{t('Done')}
			</ViewportAttachedButton>
		</div>
	);
});

export default PaymentConfiguration;
